import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp, FiCheck, FiX } from "react-icons/fi";
import "./HarrisChainOfThought.scss";

const formatElapsed = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0s";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const rem = seconds % 60;
    return `${mins}m ${rem}s`;
};

const HarrisChainOfThought = ({ steps = [], startedAt = null }) => {
    const normalizedSteps = useMemo(
        () =>
            steps.map((step) =>
                typeof step === "string"
                    ? { title: step, items: [], state: "active" }
                    : {
                        title: step?.title ?? "",
                        items: step?.items ?? [],
                        state: step?.state ?? "active",
                    }
            ),
        [steps]
    );

    const [open, setOpen] = useState(
        normalizedSteps.map((_, idx) => idx === normalizedSteps.length - 1)
    );

    useEffect(() => {
        setOpen(normalizedSteps.map((_, idx) => idx === normalizedSteps.length - 1));
    }, [normalizedSteps]);

    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    useEffect(() => {
        if (!startedAt) {
            setElapsedSeconds(0);
            return;
        }
        const tick = () => {
            const now = Date.now();
            setElapsedSeconds(Math.max(0, Math.floor((now - startedAt) / 1000)));
        };
        tick();
        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [startedAt]);

    // The list scrolls rather than growing, so keep the newest step in view.
    const stepsRef = useRef(null);
    useEffect(() => {
        const el = stepsRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [normalizedSteps.length]);

    if (!normalizedSteps.length) return null;

    const toggle = (idx) => {
        setOpen((prev) => prev.map((isOpen, i) => (i === idx ? !isOpen : isOpen)));
    };

    const activeIndex = normalizedSteps.length - 1;
    const activeStep = normalizedSteps[activeIndex];

    return (
        <div className="harris-cot">
            <div className="cot-header" role="status" aria-live="polite">
                <div className="cot-header__left">
                    <span className="cot-header__title">Working on your request</span>
                </div>
                <div className="cot-header__right">
                    <span className="cot-header__progress">{`${activeIndex + 1}/${normalizedSteps.length}`}</span>
                    <span className="cot-header__elapsed">{formatElapsed(elapsedSeconds)}</span>
                </div>
            </div>
            <div className="cot-steps" ref={stepsRef}>
            {normalizedSteps.map((step, index) => {
                const hasItems = step.items && step.items.length > 0;
                const isOpen = open[index];
                const isActiveStep = index === activeIndex;
                const stepState = step.state || (isActiveStep ? "active" : "done");
                const isPending = !isActiveStep && stepState !== "done" && stepState !== "error";

                return (
                    <div className="cot-step" key={index}>
                        <div className="cot-left">
                            <span
                                className={`cot-node ${isActiveStep ? "is-active" : ""} ${stepState === "done" ? "is-done" : ""
                                    } ${stepState === "error" ? "is-error" : ""} ${isPending ? "is-pending" : ""
                                    }`}
                            >
                                {stepState === "done" && <FiCheck className="cot-node-icon" />}
                                {isActiveStep && (
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cot-spinner">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
                                    </svg>
                                )}
                                {stepState === "error" && <FiX className="cot-node-icon" />}
                            </span>
                            {index < normalizedSteps.length - 1 && <span className="cot-connector" />}
                        </div>
                        <div
                            className={`cot-body ${isActiveStep ? "is-active" : ""} ${stepState === "done" ? "is-done" : ""
                                } ${stepState === "error" ? "is-error" : ""} ${isPending ? "is-pending" : ""
                                }`}
                        >
                            <button
                                type="button"
                                className="cot-trigger is-toggle"
                                onClick={() => toggle(index)}
                                aria-expanded={isOpen}
                                aria-label="Toggle step"
                            >
                                <span>{step.title}</span>
                                {isOpen ? (
                                    <FiChevronUp className="cot-chevron" />
                                ) : (
                                    <FiChevronDown className="cot-chevron" />
                                )}
                            </button>
                            {hasItems && isOpen && (
                                <div className="cot-content">
                                    {step.items.map((item, itemIdx) => (
                                        <div
                                            className={`cot-item ${isActiveStep ? "is-animating" : ""}`}
                                            key={itemIdx}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
            </div>
        </div>
    );
};

export default HarrisChainOfThought;
